import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { array, arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';

// Utils
import { useSetLoadingTitle } from 'hooks';

// Components
import {
  Heading,
  ListTabs,
  NoAuthorizationsAvailable,
} from 'feature/loading-management/shared-components';
import HeaderButtons from './header/HeaderButtons.connector';
import CardSkeletonList from '../../card-skeleton/CardSkeletonList';
import SupplierAuthCard from './card/SupplierAuthCard';

// Utils
import { exchangeFilter, filterBy, statusFilter, typeFilter } from './SupplierFilters';
// eslint-disable-next-line import/order
import {
  cardsListStyles,
  cardsPanelContainerStyles,
  BASE_AND_CUSTOM_TABS as tabs,
} from 'feature/loading-management/utils/const';

const CardPanelBG = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.surface.light,
}));

const CardsPanel = ({
  activeSupplierAuthTab,
  currentSelection,
  exchange,
  location,
  product,
  resetCardFilters,
  setActiveSupplierAuthTab,
  state,
  status,
  supplierAuthorizations,
  supplierAuthorizationsCards,
  setSupplierAuthorizationsCards,
  supplierAuthorizationsPending,
  supplier,
  type,
}) => {
  const { viewBy } = useParams();
  const [cardHeight, setCardHeight] = useState(355);
  const title = useSetLoadingTitle(supplierAuthorizationsPending, currentSelection);

  const transducer = R.compose(
    R.filter(exchangeFilter(exchange)),
    R.filter(statusFilter(status)),
    R.filter(typeFilter(type)),
    R.filter(filterBy(location, 'location')),
    R.filter(filterBy(product, 'product')),
    R.filter(filterBy(supplier, 'supplier')),
    R.filter(filterBy(state, 'location', 'state'))
  );

  const productTransducer = R.compose(
    R.filter(exchangeFilter(exchange)),
    R.filter(statusFilter(status)),
    R.filter(typeFilter(type)),
    R.filter(filterBy(location, 'location')),
    R.filter(filterBy(supplier, 'supplier')),
    R.filter(filterBy(state, 'location', 'state'))
  );

  useEffect(() => {
    // cleanup on dismount and after any change in viewBy
    return () => {
      setSupplierAuthorizationsCards([]);
      resetCardFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  useEffect(() => {
    if (
      supplierAuthorizations &&
      // Removing the pending check is a temporary solution to CASREF-2279.
      // Since the pending flag is syncronized with supplierAuthorizations,
      // and not supplierAuthorizationsCards, it's providing no benefit other
      // than stopping the skeletons too early.
      // !supplierAuthorizationsPending &&
      !R.isEmpty(activeSupplierAuthTab)
    ) {
      viewBy === 'product'
        ? setSupplierAuthorizationsCards(R.into([], productTransducer, supplierAuthorizations))
        : setSupplierAuthorizationsCards(
            R.into([], transducer, supplierAuthorizations[activeSupplierAuthTab])
          );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSupplierAuthTab,
    exchange,
    location,
    product,
    state,
    status,
    supplierAuthorizations,
    supplierAuthorizationsPending,
    supplier,
    type,
  ]);

  const showTabs = viewBy !== 'product';

  const renderCards = () => {
    if (supplierAuthorizationsPending) return <CardSkeletonList />;
    if (R.isEmpty(supplierAuthorizationsCards)) return <NoAuthorizationsAvailable />;
    return supplierAuthorizationsCards.map((data, index) => (
      <SupplierAuthCard
        authorizationData={data}
        cardHeight={cardHeight}
        index={index}
        key={data.productAuthorizationId}
        setCardHeight={setCardHeight}
      />
    ));
  };

  const handleListTabChange = R.o(resetCardFilters, setActiveSupplierAuthTab);
  // Since ListTabs expects object this is
  // ensuring that an object is always returned
  const labelCounts =
    supplierAuthorizations && !Array.isArray(supplierAuthorizations) ? supplierAuthorizations : {};

  return (
    <CardPanelBG {...cardsPanelContainerStyles}>
      <Heading
        headerButtons={<HeaderButtons />}
        listTabs={
          <ListTabs
            activeTab={activeSupplierAuthTab}
            labelCounts={labelCounts}
            onChange={handleListTabChange}
            pendingState={supplierAuthorizationsPending}
            tabs={tabs}
          />
        }
        showTabs={showTabs}
        title={title}
      />
      <Box {...cardsListStyles} mt={showTabs ? 0 : 3} pb={showTabs ? 7 : 0}>
        {renderCards()}
      </Box>
    </CardPanelBG>
  );
};

CardsPanel.propTypes = {
  activeSupplierAuthTab: string.isRequired,
  currentSelection: shape({}),
  exchange: string.isRequired,
  filterIdentifierCount: number.isRequired,
  location: string.isRequired,
  product: string.isRequired,
  resetCardFilters: func.isRequired,
  setActiveSupplierAuthTab: func.isRequired,
  setSupplierAuthorizationsCards: func.isRequired,
  state: string.isRequired,
  status: string.isRequired,
  supplier: string.isRequired,
  supplierAuthorizations: oneOfType([array, shape({})]),
  supplierAuthorizationsCards: arrayOf(shape({})),
  supplierAuthorizationsPending: bool.isRequired,
  type: string.isRequired,
};

CardsPanel.defaultProps = {
  currentSelection: {},
  supplierAuthorizations: null,
  supplierAuthorizationsCards: null,
};

export default CardsPanel;
